import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, BarChart3, Loader2, User, ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import {
  fetchSpeakerStats,
  fetchChapterSpeakerStats,
  fetchSlokasBySpeaker,
  getErrorMessage
} from '../api/client';
import ErrorMessage from './ErrorMessage';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';
import SpeakerImage from './SpeakerImage';

const LANGUAGE = DEFAULT_LANGUAGE;

function groupSlokasByChapter(slokas) {
  const groups = {};
  slokas.forEach((sloka) => {
    const key = sloka.chapterNumber;
    if (!groups[key]) groups[key] = [];
    groups[key].push(sloka);
  });
  return Object.entries(groups)
    .map(([chapterNumber, chapterSlokas]) => ({
      chapterNumber: parseInt(chapterNumber, 10),
      slokas: chapterSlokas.sort((a, b) => a.slokaNumber - b.slokaNumber)
    }))
    .sort((a, b) => a.chapterNumber - b.chapterNumber);
}

const Speakers = () => {
  const [speakerStats, setSpeakerStats] = useState([]);
  const [chapterStats, setChapterStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [speakerSlokas, setSpeakerSlokas] = useState([]);
  const [slokasLoading, setSlokasLoading] = useState(false);
  const [slokasError, setSlokasError] = useState(null);

  const loadSpeakerData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [stats, chapterData] = await Promise.all([
        fetchSpeakerStats(),
        fetchChapterSpeakerStats()
      ]);

      setSpeakerStats(stats.sort((a, b) => b.count - a.count));
      setChapterStats(chapterData);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSpeakerSlokas = useCallback(async (speaker, chapterNumber = null) => {
    setSlokasLoading(true);
    setSlokasError(null);
    try {
      const slokas = await fetchSlokasBySpeaker(speaker, LANGUAGE, chapterNumber);
      setSpeakerSlokas(slokas);
    } catch (err) {
      setSlokasError(getErrorMessage(err));
      setSpeakerSlokas([]);
    } finally {
      setSlokasLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSpeakerData();
  }, [loadSpeakerData]);

  useEffect(() => {
    if (selectedSpeaker) {
      loadSpeakerSlokas(selectedSpeaker, selectedChapter);
    } else {
      setSpeakerSlokas([]);
    }
  }, [selectedSpeaker, selectedChapter, loadSpeakerSlokas]);

  const handleSelectSpeaker = (speaker, chapterNumber = null) => {
    setSelectedSpeaker(speaker);
    setSelectedChapter(chapterNumber);
  };

  const handleClearSelection = () => {
    setSelectedSpeaker(null);
    setSelectedChapter(null);
    setSpeakerSlokas([]);
    setSlokasError(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gita-saffron" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadSpeakerData} />;
  }

  const totalSlokas = speakerStats.reduce((sum, stat) => sum + stat.count, 0);
  const groupedSlokas = groupSlokasByChapter(speakerSlokas);

  return (
    <div className="space-y-8">
      <div className="page-header">
        <p className="page-header-om" aria-hidden="true">ॐ</p>
        <h1 className="page-header-title">Speakers</h1>
        <p className="page-header-desc">
          Discover the voices in the Bhagavad Gita. Click a speaker to see which
          slokas they speak.
        </p>
      </div>

      {selectedSpeaker ? (
        <div className="space-y-6">
          <button
            type="button"
            onClick={handleClearSelection}
            className="btn-secondary flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to all speakers
          </button>

          <div className="card-premium">
            <div className="flex items-center space-x-4 mb-6">
              <SpeakerImage
                speaker={selectedSpeaker}
                className="w-20 h-24 sm:w-24 sm:h-28 rounded-xl shadow-md border border-gita-gold/30"
                fallbackClassName="w-20 h-24 sm:w-24 sm:h-28 rounded-xl shadow-md"
                iconClassName="w-8 h-8 text-white"
              />
              <div>
                <h2 className="section-heading">{selectedSpeaker}</h2>
                <p className="text-gray-600">
                  {selectedChapter
                    ? `Chapter ${selectedChapter} · ${speakerSlokas.length} sloka${speakerSlokas.length !== 1 ? 's' : ''}`
                    : `${speakerSlokas.length} sloka${speakerSlokas.length !== 1 ? 's' : ''} across the Gita`}
                </p>
              </div>
            </div>

            {slokasLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gita-saffron" />
              </div>
            )}

            {slokasError && (
              <ErrorMessage
                message={slokasError}
                onRetry={() => loadSpeakerSlokas(selectedSpeaker, selectedChapter)}
              />
            )}

            {!slokasLoading && !slokasError && groupedSlokas.length === 0 && (
              <p className="text-gray-500 text-center py-8">No slokas found for this speaker.</p>
            )}

            {!slokasLoading && !slokasError && groupedSlokas.length > 0 && (
              <div className="space-y-8">
                {groupedSlokas.map(({ chapterNumber, slokas }) => (
                  <div key={chapterNumber}>
                    <h3 className="content-section-title flex items-center">
                      <BookOpen className="w-5 h-5 mr-2" />
                      Chapter {chapterNumber}
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({slokas.length} verse{slokas.length !== 1 ? 's' : ''})
                      </span>
                    </h3>
                    <div className="space-y-3">
                      {slokas.map((sloka) => (
                        <Link
                          key={sloka._id}
                          to={`/sloka/${sloka._id}`}
                          className="block border border-gita-gold/20 rounded-xl p-4 hover:border-gita-saffron/50 hover:bg-gita-cream/80 transition-all duration-200"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gita-maroon mb-1">
                                Verse {sloka.slokaNumber}
                              </p>
                              <p className="text-sm text-gray-600 line-clamp-2 sloka-text">
                                {sloka.sloka}
                              </p>
                              <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                                {sloka.meaning}
                              </p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gita-saffron shrink-0 mt-1" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-center">
            <div className="bg-white rounded-lg shadow-md p-1">
              <button
                type="button"
                onClick={() => setActiveView('overview')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeView === 'overview'
                    ? 'bg-gita-saffron text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />
                Overview
              </button>
              <button
                type="button"
                onClick={() => setActiveView('chapters')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeView === 'chapters'
                    ? 'bg-gita-saffron text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                By Chapter
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card text-center">
              <div className="w-16 h-16 bg-gita-saffron rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <p className="stat-value">{speakerStats.length}</p>
              <p className="stat-label">Total Speakers</p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-gita-gold rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <p className="stat-value">{totalSlokas}</p>
              <p className="stat-label">Total Slokas</p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-gita-orange rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <p className="stat-value">
                {speakerStats.length > 0 ? Math.round(totalSlokas / speakerStats.length) : 0}
              </p>
              <p className="stat-label">Avg Slokas per Speaker</p>
            </div>
          </div>

          {activeView === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {speakerStats.map((stat) => (
                <button
                  key={stat.speaker}
                  type="button"
                  onClick={() => handleSelectSpeaker(stat.speaker)}
                  className="card text-left hover:shadow-lg hover:border-gita-saffron transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3 min-w-0">
                      <SpeakerImage
                        speaker={stat.speaker}
                        className="w-14 h-16 rounded-lg shadow-sm border border-gita-gold/20"
                        fallbackClassName="w-14 h-16 rounded-lg"
                        iconClassName="w-6 h-6 text-white"
                      />
                      <div>
                        <h3 className="font-semibold text-gita-maroon">{stat.speaker}</h3>
                        <p className="text-sm text-gray-600">Click to view slokas</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gita-maroon">{stat.count}</div>
                      <div className="text-sm text-gray-600">Slokas</div>
                    </div>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gita-saffron h-2 rounded-full transition-all duration-300"
                      style={{ width: `${totalSlokas > 0 ? (stat.count / totalSlokas) * 100 : 0}%` }}
                    />
                  </div>

                  <div className="text-xs text-gray-500 mt-2">
                    {totalSlokas > 0 ? ((stat.count / totalSlokas) * 100).toFixed(1) : 0}% of total slokas
                  </div>
                </button>
              ))}
            </div>
          )}

          {activeView === 'chapters' && (
            <div className="space-y-6">
              {chapterStats.map((chapter) => (
                <div key={chapter.chapterNumber} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gita-maroon">
                      Chapter {chapter.chapterNumber}
                    </h3>
                    <div className="text-sm text-gray-600">
                      {chapter.speakers.length} speaker{chapter.speakers.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {chapter.speakers
                      .sort((a, b) => b.count - a.count)
                      .map((speaker) => (
                        <button
                          key={speaker.speaker}
                          type="button"
                          onClick={() =>
                            handleSelectSpeaker(speaker.speaker, chapter.chapterNumber)
                          }
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-orange-50 hover:border hover:border-gita-saffron transition-colors text-left w-full"
                        >
                          <div className="flex items-center space-x-2 min-w-0">
                            <SpeakerImage
                              speaker={speaker.speaker}
                              className="w-10 h-12 rounded-md border border-gita-gold/15"
                              fallbackClassName="w-10 h-12 rounded-md"
                              iconClassName="w-4 h-4 text-white"
                            />
                            <span className="font-medium text-gray-800 truncate">{speaker.speaker}</span>
                          </div>
                          <span className="text-gita-maroon font-semibold">{speaker.count}</span>
                        </button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Speakers;
